import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AuthContext } from '../../context/AuthContext';
import { FcGoogle } from 'react-icons/fc'; // Import Google icon

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, isAuthenticated, error, clearError } = useContext(AuthContext);
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { email, password } = formData;
  
  useEffect(() => {
    // Check if there's a meeting URL in the search params
    const searchParams = new URLSearchParams(location.search);
    const meetingUrl = searchParams.get('meetingUrl');
    
    if (meetingUrl) {
      // Store the meeting URL in localStorage
      localStorage.setItem('pendingMeetingUrl', meetingUrl);
    }
    
    if (isAuthenticated) {
      // Check if there's a pending meeting URL
      const pendingMeetingUrl = localStorage.getItem('pendingMeetingUrl');
      
      if (pendingMeetingUrl) {
        // Clear the pending meeting URL
        localStorage.removeItem('pendingMeetingUrl');
        // Redirect to the meeting
        window.location.href = pendingMeetingUrl;
      } else {
        // Redirect to home
        navigate('/');
      }
    }
  }, [isAuthenticated, navigate, location.search]);
  
  // Add this useEffect to the Login component
  useEffect(() => {
    // Check if there's a token in the URL hash
    if (window.location.hash.includes('token=')) {
      console.log('Token found in URL hash on Login page');
      // Extract the token
      const tokenMatch = window.location.hash.match(/[#&?]token=([^&]+)/);
      const token = tokenMatch ? tokenMatch[1] : null;
      
      if (token) {
        console.log('Token extracted on Login page:', token);
        // You can either:
        // 1. Let AuthContext handle it (it should already be doing this)
        // 2. Or manually set the token and redirect
        localStorage.setItem('token', token);
        window.location.href = '/#/';
      }
    }
  }, []);
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear field-specific error when user types
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: null });
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!email) {
      errors.email = 'Email is required';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Add this to the onSubmit function
  const onSubmit = async e => {
    e.preventDefault();
    clearError();
    
    if (validateForm()) {
      setIsSubmitting(true);
      const success = await login({
        email,
        password
      });
      
      setIsSubmitting(false);
      
      if (success) {
        // The redirection will be handled by the useEffect above
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (err) {
      console.error('Google login error:', err);
    }
  };
  
  return (
    <LoginContainer>
      <FormCard>
        <h2>Login</h2>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Form onSubmit={onSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={onChange}
              error={formErrors.email}
              autocomplete="email" // Add this line
            />
            {formErrors.email && <ErrorText>{formErrors.email}</ErrorText>}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <div style={{ position: 'relative' }}>
              <Input
                type={formData.showPassword ? "text" : "password"}
                name="password"
                id="password"
                value={password}
                onChange={onChange}
                error={formErrors.password}
                autocomplete="current-password" // Add this line
              />
              <span 
                onClick={() => setFormData({
                  ...formData,
                  showPassword: !formData.showPassword
                })}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer'
                }}
              >
                {formData.showPassword ? "👁️" : "👁️‍🗨️"}
              </span>
            </div>
            {formErrors.password && <ErrorText>{formErrors.password}</ErrorText>}
          </FormGroup>
          
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Logging in...' : 'Login'}
          </Button>
        </Form>
        
        <OrDivider>
          <span>OR</span>
        </OrDivider>
        
        <GoogleButton onClick={handleGoogleLogin}>
          <FcGoogle size={20} />
          <span>Continue with Google</span>
        </GoogleButton>
        
        <RegisterLink>
          Don't have an account? <Link to="/register">Register</Link>
        </RegisterLink>
      </FormCard>
    </LoginContainer>
  );
};

// Styled components
const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  
  h2 {
    text-align: center;
    margin-bottom: 1.5rem;
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => (props.error ? '#ff3860' : '#ddd')};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4267B2;
  }
`;

const Button = styled.button`
  background-color: #4267B2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  
  &:hover {
    background-color: #365899;
  }
  
  &:disabled {
    background-color: #9cb4d8;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  text-align: center;
`;

const ErrorText = styled.span`
  color: #ff3860;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: block;
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  
  a {
    color: #4267B2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// New styled components for Google button
const OrDivider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1.5rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #ddd;
  }
  
  span {
    padding: 0 10px;
    color: #777;
    font-size: 0.9rem;
  }
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  span {
    margin-left: 10px;
  }
  
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    background-color: #f8f8f8;
  }
`;

export default Login;